/**
 * Hook para gerenciar a instalação do PWA
 * 
 * Funcionalidades:
 * - Detecta evento beforeinstallprompt
 * - Verifica se o app já está instalado
 * - Provê função para acionar prompt de instalação
 * - Detecta se está rodando em modo standalone (instalado)
 */
import { useState, useEffect, useCallback } from 'react';

// Interface para o evento de instalação do PWA
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Interface de retorno do hook
interface PWAInstallState {
  // Indica se o prompt de instalação está disponível
  isInstallable: boolean;
  // Indica se o app já está instalado (modo standalone)
  isInstalled: boolean;
  // Indica se é um dispositivo iOS (requer instruções manuais)
  isIOS: boolean;
  // Função para acionar o prompt de instalação
  promptInstall: () => Promise<boolean>;
  // Indica se a instalação está em progresso
  isInstalling: boolean;
}

export function usePWAInstall(): PWAInstallState {
  // Estado do evento de instalação (apenas disponível no Chrome/Android)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  // Indica se o prompt está disponível
  const [isInstallable, setIsInstallable] = useState(false);
  // Indica se está instalando
  const [isInstalling, setIsInstalling] = useState(false);

  // Detecta se é iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  // Verifica se o app está rodando em modo standalone (instalado)
  const isInstalled = 
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://');

  useEffect(() => {
    // Handler para o evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      // Previne o mini-infobar automático no mobile
      e.preventDefault();
      // Armazena o evento para uso posterior
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    // Handler para quando o app é instalado
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
    };

    // Registra os listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Função para acionar o prompt de instalação
  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    setIsInstalling(true);

    try {
      // Mostra o prompt de instalação
      await deferredPrompt.prompt();
      
      // Aguarda a escolha do usuário
      const { outcome } = await deferredPrompt.userChoice;
      
      // Limpa o evento após uso (só pode ser usado uma vez)
      setDeferredPrompt(null);
      setIsInstallable(false);
      
      return outcome === 'accepted';
    } catch (error) {
      console.error('[PWA] Erro ao instalar:', error);
      return false;
    } finally {
      setIsInstalling(false);
    }
  }, [deferredPrompt]);

  return {
    isInstallable,
    isInstalled,
    isIOS,
    promptInstall,
    isInstalling
  };
}
